<template>
  <!-- On mobile the preview sits between the intro and the benefits, or after the content when the benefits are below; on desktop it spans the rows beside them -->
  <GridContainer
    class="pt-20 md:pt-24 lg:pt-28"
    :ui="{ base: `${align === 'start' ? 'md:grid-rows-[auto_0_auto_auto_1fr]' : 'md:grid-rows-[auto_1fr_auto_auto_1fr]'} ${hasBenefitsBelow ? 'flex flex-col' : ''}` }"
  >
    <div
      v-if="$slots.top"
      class="md:col-span-2 md:row-start-1"
    >
      <slot name="top" />
    </div>

    <div class="min-w-0 md:col-start-1 md:row-start-3 md:pr-12 lg:pr-16">
      <Heading
        :type="headingType"
        variant="h2"
        :flourish="eyebrow ?? $t('cobranded_supplier_websites_eyebrow', 'Make their website yours')"
        flourish-color="brand"
        :ui="{ flourish: 'uppercase' }"
      >
        <slot name="title">
          {{ $t('cobranded_supplier_websites_title', 'Co-Branded Supplier Websites') }}
        </slot>
      </Heading>
      <p class="mt-3 max-w-2xl">
        {{ description ?? $t('cobranded_supplier_websites_description', 'Turn your favorite supplier website into your own sales platform.') }}
      </p>
    </div>

    <div
      class="min-w-0 md:col-span-2 md:col-start-2 md:row-[2/6] md:mt-0 md:pl-12 lg:pl-16"
      :class="[
        align === 'start' ? 'md:self-start' : 'md:self-center',
        hasBenefitsBelow ? 'mt-12 max-md:order-1' : 'mt-8'
      ]"
    >
      <Motion
        :initial="{ opacity: 0, y: '3rem' }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.5 }"
      >
        <CopilotPreview
          :partner="partner"
          :advisor="previewAdvisor ?? placeholderAdvisor"
          :insider="previewAdvisor ? getInsiderParam(previewAdvisor) : undefined"
          class="select-none md:rounded-r-none"
        >
          <template
            v-if="$slots.preview"
            #default
          >
            <slot name="preview" />
          </template>
        </CopilotPreview>
      </Motion>
    </div>

    <div class="mt-10 flex min-w-0 flex-col space-y-8 md:col-start-1 md:row-start-4 md:mt-8 md:pr-12 lg:pr-16">
      <CopilotBenefits
        v-if="!hasBenefitsBelow"
        :is-brand-voice="isBrandVoice"
      />
      <slot />
    </div>

    <CopilotBenefits
      v-if="hasBenefitsBelow"
      :is-brand-voice="isBrandVoice"
      :domain="partner.site.domain.replace(/^www\./, '')"
      detailed
      class="mt-16 min-w-0 max-md:order-2 md:col-span-2 md:row-start-6 md:mt-20 lg:mt-28"
    />
  </GridContainer>
</template>

<script setup lang="ts">
import type CopilotPreview from './CopilotPreview.vue'

type PreviewProps = InstanceType<typeof CopilotPreview>['$props']

const props = withDefaults(defineProps<{
  partner: PreviewProps['partner']
  advisor?: PreviewProps['advisor'] & { hashid: string | null }
  align?: 'start' | 'center'
  headingType?: 'h1' | 'h2'
  eyebrow?: string
  description?: string
  isBrandVoice?: boolean
  // Spreads the benefits, with descriptions, across the width beneath the preview
  hasBenefitsBelow?: boolean
}>(), {
  advisor: undefined,
  align: 'center',
  headingType: 'h2',
  eyebrow: undefined,
  description: undefined
})

const { t } = useI18n()
const user = useUser()

const previewAdvisor = computed(() => props.advisor ?? user.value)

const placeholderAdvisor = computed(() => ({
  first_name: null,
  last_name: null,
  phone: t('your_phone', 'Your phone'),
  logo: null,
  personal_photo: { full_url: '/avatar-jennifer-raezer.png' }
}))
</script>
